/**
 * SandboxDemoHero — a lightweight, safe, pre-seeded PM Copilot preview
 * that lives inside the marketing hero on the landing page.
 *
 * Zero backend calls. Pre-computed tenant tickets and staged copilot
 * responses cycle in a typewriter effect so a visitor can see what
 * the copilot does without signing up.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, ArrowRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONVO = [
  {
    tenant_name: "Unit 4B",
    ticket: "The kitchen faucet has been dripping for two days and the cabinet under the sink is getting damp.",
    copilot_reply:
      "Thanks for the heads-up — that sounds like a worn faucet cartridge, and the damp cabinet means we should get someone in today. I've flagged this as high priority and asked our plumber to book a same-day slot. You'll get a text confirmation within the hour.",
    tags: ["High priority", "Plumber · same-day", "Parts: faucet cartridge"],
  },
  {
    tenant_name: "Unit 12",
    ticket: "Rent is going to be a few days late this month — I picked up fewer shifts. Please don't file anything yet.",
    copilot_reply:
      "Thanks for telling us before the due date — life happens, and we're not filing anything. Would a 2-week installment plan work — $875 next Friday and $875 the Friday after? If that's still tight, Ontario Trillium Benefit has a rent-assist top-up you may qualify for — happy to send the link.",
    tags: ["Empathetic tone", "Payment plan · 2-week", "ON assistance program"],
  },
  {
    tenant_name: "Unit 7",
    ticket: "Where do I find the parking rules? My friend visited and the tow truck came.",
    copilot_reply:
      "Sorry about that — the visitor parking rules are in the lease under Schedule B, and I've just resent it to your email. In short: visitors can park in spots 15-22 with a dashboard permit from the office. I'll leave a permit in your mailbox for future visits.",
    tags: ["Lease reference", "Follow-up scheduled"],
  },
];

export default function SandboxDemoHero() {
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [showTags, setShowTags] = useState(false);
  const [typing, setTyping] = useState(true);

  useEffect(() => {
    setTyped(""); setShowTags(false); setTyping(true);
    const reply = CONVO[idx].copilot_reply;
    let i = 0;
    // Human-natural typing pace — ~25 chars/sec (was ~150). Slow enough
    // to read along, fast enough that the next reveal doesn't feel
    // sluggish. Adjusting? Keep step * (1000/interval) ≈ 25.
    const iv = setInterval(() => {
      i += 1;
      setTyped(reply.slice(0, i));
      if (i >= reply.length) {
        clearInterval(iv);
        setTyping(false);
        setShowTags(true);
        setTimeout(() => setIdx((idx + 1) % CONVO.length), 6500);
      }
    }, 40);
    return () => clearInterval(iv);
  }, [idx]);

  const c = CONVO[idx];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden w-full max-w-full" data-testid="sandbox-demo-hero">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
            <Bot className="w-3.5 h-3.5 text-emerald-700" />
          </div>
          <div className="text-xs uppercase tracking-wide font-bold text-slate-600">
            Copilot preview — live demo, no signup
          </div>
        </div>
        <div className="flex gap-1">
          {CONVO.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)}
              className={`w-1.5 h-1.5 rounded-full transition ${i === idx ? "bg-emerald-600 w-4" : "bg-slate-300"}`}
              data-testid={`sandbox-dot-${i}`}
              aria-label={`Show conversation ${i + 1}`} />
          ))}
        </div>
      </div>
      <div className="p-4 space-y-3 min-h-[260px]">
        <div className="flex gap-2" data-testid="sandbox-tenant-msg">
          <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center flex-none">
            <MessageCircle className="w-3.5 h-3.5 text-sky-700" />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-900">{c.tenant_name}</div>
            <div className="text-sm font-light text-slate-700 mt-0.5 leading-relaxed">{c.ticket}</div>
          </div>
        </div>
        <div className="flex gap-2" data-testid="sandbox-copilot-msg">
          <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-none">
            <Bot className="w-3.5 h-3.5 text-emerald-700" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-medium text-emerald-700">Central Rentals Copilot</div>
            <div className="text-sm font-light text-slate-800 mt-0.5 leading-relaxed">
              {typed}
              {typing && (
                <span
                  aria-hidden="true"
                  className="inline-block align-text-bottom w-[1.5px] h-[1.05em] ml-[1px] bg-emerald-600 animate-pulse"
                  data-testid="copilot-caret"
                />
              )}
            </div>
            {showTags && (
              <div className="flex flex-wrap gap-1 mt-2" data-testid="sandbox-tags">
                {c.tags.map((t) => (
                  <span key={t} className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="px-4 pb-4">
        <Link to="/signup">
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="sandbox-cta">
            Try Copilot with your own tenants — Paid plans from CA$35/month
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
